// The relay worker's message handling, apart from its iroh endpoint so tests
// can run it (relay-worker.ts gives it the endpoint): tabs' requests to an
// agent, and serving the subject tools (docs/superpowers/specs/2026-10-23-relay-pr-connections-design.md).
//
// While at least one tab of a user is attached, and while this worker holds
// the user's endpoint without having taken it from another deploy's worker,
// the worker runs one `serve` loop per pairing. Each iteration first resends
// the unacknowledged grant changes, then waits for queries and grant
// requests. A query goes to one tab that volunteered (`serving`), in turn,
// or to the tab holding the paging snapshot it continues.
//
// Deadlines: the helper gives up on a query a while after it parked it, and
// hands each query out with the time left (`expires_in_ms`). That is the one
// deadline; the others nest inside it. The worker answers the helper by it,
// less SEND_MS for the answer to arrive, and gives the tab that much less
// POST_MS for its own answer to come back, so the tab's reads stop first. A
// tab says it took the query (`received`) at once: one that doesn't within
// RECEIPT_MS is skipped and passed over until it next answers, and the query
// goes to the next tab with what is left. A tab that took it is reading, so
// it is not passed over: if the worker's deadline comes first, the agent is
// told Changeling is still reading (STILL_READING), not that no tab serves.
// Grant requests go to every attached tab. The worker checks a query
// against the local grants before handing it to a tab and again before
// sending the tab's answer, so a revoked grant is never answered for.
//
// Signing out stops the user (`stop`): a new generation, so no loop or
// answer already under way reads the user's storage, which would recreate
// it, or sends anything.
import { read, write } from './relay-db';
import { GRAPHQL_BUDGET, GraphqlBudget, RELAY_BUDGET_STORE } from './relay-budget';
import { locatorText, parseLocator, readKey, sameSubject } from './relaySubject';
import { errorMessage, liveGrants, locked, refusal, requestCode, REQUEST_TTL_MS, seenKey, sync, type GrantRequest, type RelayConnection, type RelayQuery, type WireGrantRequest } from './relay-grants';

/** The tab's end of the worker, or a test's stand-in. */
export interface Port { postMessage(message: unknown): void }
/** The helper's reply to one request. */
export type Reply = { result: unknown } | { error: string; code?: string };
export interface ServeDeps {
  /**
   * Sends `message` to the agent at `peer` and resolves with its reply;
   * rejects when it can't be reached. `background`: serving's own request,
   * which must not take the endpoint from another worker.
   */
  request(user: string, peer: string, relay: string | undefined, message: unknown, background?: boolean): Promise<Reply>;
  /** Closes the user's endpoint (sign-out, identity reset). */
  close(user: string): Promise<void>;
  /**
   * Resolves once this worker holds the user's endpoint without taking it
   * from another worker; `lost` settles when it no longer holds it.
   */
  acquire(user: string): Promise<{ lost: Promise<void> }>;
  /**
   * Gives the endpoint up for serving: the user's last tab left. With
   * `ifWanted`, only when another context is queued for it.
   */
  release?(user: string, ifWanted?: boolean): void;
  /** Resolves after `ms`, unless `signal` aborts first. */
  delay(ms: number, signal?: AbortSignal): Promise<void>;
  /** The paging snapshot `q` continues, whose tab should answer it; `snapshotOf` by default. */
  affinity?(q: RelayQuery): string | null;
  /** This Changeling's origin, sent with grants. */
  origin?: string;
  /** The clock deadlines and the GraphQL budget are counted by; Date.now by default. */
  now?: () => number;
  /** Every tab's GraphQL pages, counted here once for all of them and kept in the user's relay database (relay-budget.ts). */
  budget?: GraphqlBudget;
}
/** A query as `serve` returns it, before it is checked: `expires_in_ms`, the time left before the helper gives up on it. */
interface WireQuery { id: string; tool: unknown; subject: unknown; args?: unknown; expires_in_ms?: unknown }

export const SERVE_WAIT_MS = 12000;
/** A `results` cursor, as a serve says the pairing's newest result: `<updated>:<task id>` (the helper's task.mjs). */
export const RESULTS_CURSOR = /^\d{1,15}:[a-zA-Z0-9-]{1,80}$/;
/** The time a helper that names none gives a query: its QUERY_TIMEOUT (plugins/changeling/scripts/store.mjs). */
export const QUERY_MS = 30_000;
/**
 * Kept back from the helper's deadline for the worker's answer to reach it:
 * one request over the open iroh connection (a relayed round trip is well
 * under a second), and the helper's clock, which started the deadline when it
 * parked the query, ran ahead of ours by the serve reply's way here.
 */
export const SEND_MS = 1_000;
/** Kept back from the worker's deadline for a tab's answer to reach the worker. */
export const POST_MS = 1_000;
/** How long a tab has to say it took a query before the next tab is asked. */
export const RECEIPT_MS = 2_000;
const BACKOFF_MS = 1000, BACKOFF_MAX_MS = 30000;
/**
 * How far an agent's last seen must move on before it is written again: a
 * serve returns every SERVE_WAIT_MS, and last seen is shown to the minute.
 */
export const SEEN_WRITE_MS = 60_000;
/** How long a grant request this browser settled stays settled, though a serve already under way still lists it. */
const SETTLED_MS = 60_000;
/** The helper's wire limit, less room for the answer's envelope. */
export const ANSWER_LIMIT = 1024 * 1024 - 1024;
export const ERROR_LIMIT = 1024;
/** Paging snapshots whose tab is remembered. */
const SNAPSHOTS = 500;
export const NO_TAB = 'No Changeling tab is serving this PR. Open Changeling in your browser.';
/** Serving tabs there are, but none took the query while there was time to answer it. */
export const TOO_LATE = 'This query reached Changeling too late to be answered in time. Ask again.';
/** A tab took the query, and was still reading it when time ran out. */
export const STILL_READING = 'Changeling is still reading this from GitHub and did not finish in time. Ask again in a moment.';
export const TOO_LARGE = 'Answer too large';

/** Why a user stops: their identity reset, after which their tabs attach again, or a sign-out. */
export type StopReason = 'reset' | 'signout';
/** Messages from a tab: `id` ones get a reply, the others don't. */
export type TabMessage =
  | { id: string; user: string; op: 'request'; peer: string; relay?: string; message: unknown }
  | { id: string; user: string; op: 'close'; reason?: StopReason }
  | { op: 'attach'; user: string; serving: boolean }
  | { op: 'detach' }
  | { op: 'changed'; user: string }
  | { id: string; op: 'ping' }
  | { id: string; op: 'spend'; qid: string }
  | { op: 'received'; qid: string }
  | { op: 'answered'; qid: string; skip?: boolean; result?: unknown; error?: string };

/**
 * The snapshot id a paging cursor names, or null: the grammar of the
 * tool handlers (relayTools.ts), the tool's letter (c comments, f files, k checks, d diff, m commits), the snapshot's
 * 12 hex digits and a base-36 position.
 */
export function cursorSnapshot(cursor: unknown): string | null {
  const m = typeof cursor === 'string' ? /^([cfkdm])\.([0-9a-f]{12})\.([0-9a-z]{1,8})$/.exec(cursor) : null;
  return m ? m[2] : null;
}
export const snapshotOf = (q: RelayQuery): string | null => cursorSnapshot(q.args.cursor);

const bytes = (s: string) => new TextEncoder().encode(s).byteLength;
/** `s` cut to at most `max` bytes of UTF-8. */
function cut(s: string, max: number): string {
  if (bytes(s) <= max) return s;
  let out = '', size = 0;
  for (const ch of s) { const n = bytes(ch); if (size + n > max - 3) break; out += ch; size += n; }
  return out + '…';
}

/** Thrown inside work whose user was stopped or whose endpoint was lost. */
class Stopped extends Error {}
interface Tab { user: string; serving: boolean; demoted: boolean }
interface Loop { wake: () => void }
interface UserState {
  gen: number;
  /** Signed out: nothing runs until a tab attaches or asks again. */
  stopped: boolean;
  hold?: 'waiting' | 'held';
  holdToken: number;
  /** The last tab left: the endpoint is given up once the loops still running, and their answers, are done. */
  releasing: boolean;
  /** Loops running, the ones ending included. */
  running: number;
}
type Waiting = { port: Port; user: string; connection: string; resolve: (r: Reply | null | 'stopped') => void; received: () => void };

export class Server {
  private tabs = new Map<Port, Tab>();
  private users = new Map<string, UserState>();
  private loops = new Map<string, Loop>();
  /** Grant requests waiting, by user, then by `connection\0id`, with when they arrived. */
  private requests = new Map<string, Map<string, GrantRequest & { received: number }>>();
  /**
   * Grant requests this browser settled, or that expired, by user, then by
   * `connection\0id`: when, and the `created` they were listed with (an
   * agent asking again changes it).
   */
  private settled = new Map<string, Map<string, { at: number; created?: number }>>();
  /** The tab holding each paging snapshot (`user\0id`). */
  private snapshots = new Map<string, Port>();
  private waiting = new Map<string, Waiting>();
  private running = new Set<Promise<unknown>>();
  private turn = 0;
  /** The last seen this worker wrote or found stored, by `user\0connection`. */
  private seen = new Map<string, number>();
  private budget: GraphqlBudget;
  constructor(private deps: ServeDeps) { this.budget = deps.budget ?? new GraphqlBudget(GRAPHQL_BUDGET, RELAY_BUDGET_STORE); }
  private now(): number { return (this.deps.now ?? Date.now)(); }

  /** Settles once every loop and answer under way has ended (tests). */
  async drained(): Promise<void> { while (this.running.size) await Promise.allSettled([...this.running]); }
  private track(p: Promise<unknown>) { this.running.add(p); void p.finally(() => this.running.delete(p)); }

  async receive(port: Port, data: TabMessage): Promise<void> {
    switch (data.op) {
      case 'attach': {
        const old = this.tabs.get(port);
        Object.assign(this.state(data.user), { stopped: false, releasing: false });
        this.tabs.set(port, { user: data.user, serving: data.serving, demoted: old?.user === data.user && old.demoted });
        if (old && old.user !== data.user) this.abandon(port, old.user);
        if (old?.user !== data.user) this.replay(port, data.user);
        if (old?.user !== data.user || old.serving !== data.serving) void this.changed(data.user);
        return;
      }
      case 'detach': this.leave(port); return;
      // A tab checking that this worker is still there.
      case 'ping': port.postMessage({ id: data.id, result: null }); return;
      // A tab about to read a GraphQL page for a query it is answering.
      case 'spend': {
        const w = this.waiting.get(data.qid);
        if (w?.port !== port) { port.postMessage({ id: data.id, error: TOO_LATE }); return; }
        // Granted only once written to the user's relay database, which a restarted worker reads first.
        const why = await this.budget.spend(w.user, w.connection, this.now());
        port.postMessage(why ? { id: data.id, error: why, code: 'budget' } : { id: data.id, result: null });
        return;
      }
      case 'changed':
        this.broadcast(data.user, { event: 'grants' }, port);
        void this.changed(data.user);
        return;
      case 'received': {
        const tab = this.tabs.get(port);
        if (tab) tab.demoted = false;
        const w = this.waiting.get(data.qid);
        if (w?.port === port) w.received();
        return;
      }
      case 'answered': {
        const tab = this.tabs.get(port);
        if (tab) tab.demoted = false;
        const w = this.waiting.get(data.qid);
        if (w?.port === port) w.resolve(data.skip ? null : data.error !== undefined ? { error: data.error } : { result: data.result ?? null });
        return;
      }
    }
    try {
      if (data.op === 'close') {
        this.stop(data.user, data.reason ?? 'signout');
        await this.deps.close(data.user);
        port.postMessage({ id: data.id, result: null });
        return;
      }
      // A tab asking is the user back, after a sign-out in another tab.
      this.state(data.user).stopped = false;
      const m = data.message as { op?: string; route?: string };
      let reply: Reply;
      try { reply = await this.deps.request(data.user, data.peer, data.relay, data.message); }
      finally {
        // A tab that isn't attached (no listeners: Settings, a thread
        // panel) took the endpoint for this request: give it up again if
        // another worker is queued for it, so the worker whose tabs are
        // attached serves. Otherwise keep it for the next request.
        if (!this.attached(data.user).length && !this.state(data.user).hold) this.deps.release?.(data.user, true);
      }
      if (!('error' in reply)) this.settle(data.user, `${data.peer}/${m.route}`, m);
      port.postMessage({ id: data.id, ...reply });
    } catch (e) { port.postMessage({ id: data.id, error: errorMessage(e) }); }
  }

  /** A tab closed its port or went away. */
  leave(port: Port): void {
    const tab = this.tabs.get(port);
    this.tabs.delete(port);
    if (tab) this.abandon(port, tab.user);
  }
  /**
   * Passes `port`'s unanswered queries for `user` on to another tab; with
   * no tab of `user` left, stops serving them and gives the endpoint up,
   * so another worker's tabs are served.
   */
  private abandon(port: Port, user: string) {
    for (const w of this.waiting.values()) if (w.port === port && w.user === user) w.resolve(null);
    if (this.attached(user).length) return;
    const s = this.state(user);
    if (!s.hold) return;
    s.releasing = true;
    this.endLoops(user);
    this.releaseIfIdle(user);
  }
  /** Gives the endpoint up once a user with no tab has no loop left (each waits for its answers). */
  private releaseIfIdle(user: string) {
    const s = this.state(user);
    if (!s.releasing || s.running || this.attached(user).length) return;
    s.releasing = false; s.hold = undefined; s.holdToken++;
    this.deps.release?.(user);
  }

  /**
   * Stops serving `user`: signed out, or its identity reset. Its tabs are
   * told (`stopped`, with the reason) and detached; after a reset, one
   * still signed in as `user` attaches again, which resumes serving.
   */
  stop(user: string, reason: StopReason = 'signout'): void {
    const s = this.state(user);
    s.gen++; s.stopped = true; s.hold = undefined; s.holdToken++; s.releasing = false;
    this.endLoops(user);
    this.requests.delete(user); this.settled.delete(user);
    for (const k of this.seen.keys()) if (k.startsWith(`${user}\0`)) this.seen.delete(k);
    // Its database is being deleted (signed out, or the identity reset): nothing more is written to it.
    this.budget.forget(user);
    for (const k of this.snapshots.keys()) if (k.startsWith(`${user}\0`)) this.snapshots.delete(k);
    this.broadcast(user, { event: 'stopped', reason });
    for (const w of this.waiting.values()) if (w.user === user) w.resolve('stopped');
    for (const [port, tab] of this.tabs) if (tab.user === user) this.tabs.delete(port);
  }

  private state(user: string): UserState {
    let s = this.users.get(user);
    if (!s) this.users.set(user, s = { gen: 0, stopped: false, holdToken: 0, releasing: false, running: 0 });
    return s;
  }
  /** Whether work started at `gen` with endpoint hold `token` may go on. */
  private current(user: string, gen: number, token: number): boolean {
    const s = this.state(user);
    return s.gen === gen && s.holdToken === token && s.hold === 'held' && !s.stopped;
  }
  private endLoops(user: string) {
    for (const [key, loop] of this.loops) if (key.startsWith(`${user}\0`)) { this.loops.delete(key); loop.wake(); }
  }
  private attached(user: string): Port[] { return [...this.tabs].filter(([, t]) => t.user === user).map(([p]) => p); }
  private broadcast(user: string, event: object, except?: Port) {
    for (const port of this.attached(user)) if (port !== except) port.postMessage({ ...event, user });
  }

  /** Waits for the user's endpoint, without taking it from another worker, then serves. */
  private acquire(user: string) {
    const s = this.state(user);
    if (s.hold) return;
    s.hold = 'waiting';
    const token = ++s.holdToken, gen = s.gen;
    const wanted = () => s.holdToken === token && s.gen === gen;
    this.deps.acquire(user).then(({ lost }) => {
      if (!wanted()) return;
      s.hold = 'held';
      void lost.then(() => {
        if (!wanted()) return;
        // Another worker took it: stop, and queue for it again.
        s.hold = undefined; s.holdToken++; s.releasing = false;
        this.endLoops(user);
        if (this.attached(user).length && !s.stopped) this.acquire(user);
      });
      void this.changed(user);
    }, () => {
      if (!wanted()) return;
      s.hold = undefined;
      void this.deps.delay(BACKOFF_MS).then(() => { if (!s.hold && !s.stopped && s.gen === gen && this.attached(user).length) this.acquire(user); });
    });
  }

  /**
   * Starts the loops `user`'s pairings need and wakes the running ones.
   * Never rejects: storage that can't be read (being deleted, say) is tried
   * again after a second.
   */
  private async changed(user: string): Promise<void> {
    const s = this.state(user);
    if (!this.attached(user).length || s.stopped) return;
    if (s.hold !== 'held') { this.acquire(user); return; }
    const gen = s.gen, token = s.holdToken;
    let connections: RelayConnection[];
    try { connections = await read<RelayConnection[]>(user, 'connections') ?? []; }
    catch {
      await this.deps.delay(BACKOFF_MS);
      if (this.current(user, gen, token)) void this.changed(user);
      return;
    }
    if (!this.current(user, gen, token)) return;
    for (const c of connections) {
      const key = `${user}\0${c.id}`;
      const running = this.loops.get(key);
      if (running) running.wake();
      else this.track(this.loop(user, c, key, gen, token));
    }
  }

  private async loop(user: string, c: RelayConnection, key: string, gen: number, token: number): Promise<void> {
    let wake!: () => void, woken = new Promise<void>(r => { wake = r; });
    const self: Loop = { wake: () => { const w = wake; woken = new Promise<void>(r => { wake = r; }); w(); } };
    this.loops.set(key, self);
    const live = () => this.loops.get(key) === self && this.current(user, gen, token) && this.attached(user).length > 0;
    const guard = () => { if (!live()) throw new Stopped(); };
    const send = async (msg: Record<string, unknown>) => {
      guard();
      const reply = await this.deps.request(user, c.peer, c.relay, { ...msg, route: c.route }, true);
      if ('error' in reply) throw Object.assign(new Error(reply.error), { code: reply.code });
      if (msg.op === 'grant') this.settle(user, c.id, msg);
      return reply.result;
    };
    let backoff = BACKOFF_MS;
    // The first serve returns at once with what is waiting, as after this worker started.
    let wait = 0;
    const answering = new Set<Promise<void>>();
    const s = this.state(user);
    s.running++;
    while (live()) {
      try {
        guard();
        if (!(await read<RelayConnection[]>(user, 'connections') ?? []).some(x => x.id === c.id)) break;
        if (await sync(user, c.id, send, { guard, origin: this.deps.origin })) this.broadcast(user, { event: 'grants' });
        const result = await send({ op: 'serve', wait_ms: wait }) as { queries?: WireQuery[]; requests?: WireGrantRequest[]; seen_ago_ms?: unknown; results?: unknown } | null;
        wait = SERVE_WAIT_MS; backoff = BACKOFF_MS;
        // Stopped or the endpoint lost: drop it all. The last tab gone: the
        // queries are answered still (that no tab serves), before the
        // endpoint is given up.
        if (!this.current(user, gen, token)) break;
        const requests = Array.isArray(result?.requests) ? result.requests : [];
        const queries = Array.isArray(result?.queries) ? result.queries : [];
        this.reconcile(user, c.id, requests);
        // The pairing's newest finished range task: tabs read its results when they are behind it (state/agentNotes.ts).
        if (typeof result?.results === 'string' && RESULTS_CURSOR.test(result.results)) this.broadcast(user, { event: 'results', connection: c.id, after: result.results });
        for (const q of queries) {
          const p = this.answer(user, c, q, gen, token);
          answering.add(p); void p.finally(() => answering.delete(p));
          this.track(p);
        }
        // A query is the agent at work now; else the helper says when its
        // session last checked in, if it knows. (A grant request isn't: the
        // helper lists one at every serve until it is settled or expires.)
        const ago = result?.seen_ago_ms, now = this.now();
        const at = queries.length ? now : typeof ago === 'number' && Number.isFinite(ago) ? now - Math.max(0, ago) : undefined;
        if (at !== undefined) await this.see(user, c.id, at, guard);
      } catch (e) {
        if (e instanceof Stopped || !live()) break;
        await Promise.race([this.deps.delay(backoff), woken]);
        backoff = Math.min(backoff * 2, BACKOFF_MAX_MS);
      }
    }
    if (this.loops.get(key) === self) this.loops.delete(key);
    await Promise.allSettled([...answering]);
    s.running--;
    this.releaseIfIdle(user);
  }

  /**
   * Records that `connection`'s agent was seen at `at`, once that is
   * SEEN_WRITE_MS past what this worker last knew, and tells the user's
   * tabs. Only later than what is stored (a tab writes it on pairing), and
   * only while the pairing is still there, so a pairing removed meanwhile
   * isn't given one again. Never throws but for `guard`: last seen is
   * worth no serve.
   */
  private async see(user: string, connection: string, at: number, guard: () => void) {
    const key = `${user}\0${connection}`;
    if (at < (this.seen.get(key) ?? -Infinity) + SEEN_WRITE_MS) return;
    let stored: number | null;
    try {
      stored = await locked(user, async () => {
        guard();
        if (!(await read<RelayConnection[]>(user, 'connections') ?? []).some(x => x.id === connection)) return null;
        const old = await read<number>(user, seenKey(connection));
        if (typeof old === 'number' && old >= at) return old;
        await write(user, seenKey(connection), at);
        return at;
      });
    } catch (e) { if (e instanceof Stopped) throw e; return; }
    if (stored === null) return;
    this.seen.set(key, stored);
    if (stored === at) this.broadcast(user, { event: 'seen', connection, at });
  }

  /** The user's waiting grant requests, after forgetting the expired ones. */
  private pending(user: string) {
    let map = this.requests.get(user);
    if (!map) this.requests.set(user, map = new Map());
    const now = Date.now();
    let settled = this.settled.get(user);
    if (!settled) this.settled.set(user, settled = new Map());
    for (const [k, t] of settled) if (now - t.at >= SETTLED_MS) settled.delete(k);
    for (const [k, r] of map) if (now - r.received >= REQUEST_TTL_MS) { map.delete(k); settled.set(k, { at: now, created: r.created }); this.gone(user, r); }
    return map;
  }
  private gone(user: string, r: GrantRequest) { this.broadcast(user, { event: 'grant-request-gone', id: r.id, connection: r.connection }); }
  private event(r: GrantRequest & { received?: number }) {
    const { received: _, ...request } = r;
    return { event: 'grant-request', request };
  }
  /**
   * Takes a serve's grant requests for `connection` as the whole list
   * waiting: new or changed ones go to every tab, missing ones are gone.
   */
  private reconcile(user: string, connection: string, list: WireGrantRequest[]) {
    const map = this.pending(user), settled = this.settled.get(user)!;
    const listed = new Set<string>();
    for (const r of list) {
      // A subject's key, or (an agent asking for a history by its locator) the locator, which the tab resolves.
      const subject = readKey(r?.subject), locator = subject ? null : parseLocator(r?.locator);
      if (typeof r?.id !== 'string' || r.id.length > 80 || (!subject && !locator)) continue;
      const k = `${connection}\0${r.id}`, created = Number(r.created);
      listed.add(k);
      // Settled here, or expired: kept settled while the helper lists it as
      // it was, and shown again once the agent asks again (a new `created`).
      const t = settled.get(k);
      if (t) {
        t.created ??= created;
        if (t.created === created) { t.at = Date.now(); continue; }
        settled.delete(k);
      }
      const request: GrantRequest = { id: r.id, connection, ...(subject ? { subject } : { locator: locatorText(locator!) }), fingerprint: String(r.fingerprint), name: String(r.name), created };
      const code = requestCode(r.request);
      if (code) request.request = code;
      const old = map.get(k);
      // Once resolved, a request for a locator names its key: the tabs hear of it again.
      if (old && old.created === request.created && old.request === request.request && old.subject === request.subject) continue;
      map.set(k, { ...request, received: Date.now() });
      this.broadcast(user, this.event(request));
    }
    for (const [k, r] of map) if (r.connection === connection && !listed.has(k)) { map.delete(k); this.gone(user, r); }
  }
  /** Tells a newly attached tab the grant requests still waiting. */
  private replay(port: Port, user: string) {
    for (const r of this.pending(user).values()) port.postMessage({ ...this.event(r), user });
  }
  /**
   * Forgets the grant requests a `grant` or `decline` the helper accepted
   * settles, and tells every tab; a request a `resolve` named the key of
   * is for that key from then on.
   */
  private settle(user: string, connection: string, msg: unknown) {
    const m = msg as { op?: string; request?: string; subject?: unknown };
    const granted = readKey(m.subject);
    if (m.op === 'resolve' && granted) {
      const k = `${connection}\0${m.request}`, r = this.pending(user).get(k);
      if (r?.locator !== undefined) this.pending(user).set(k, { id: r.id, connection: r.connection, subject: granted, ...(r.request ? { request: r.request } : {}), fingerprint: r.fingerprint, name: r.name, created: r.created, received: r.received });
      return;
    }
    if (m.op !== 'decline' && m.op !== 'grant') return;
    const map = this.pending(user), settled = this.settled.get(user)!;
    if (m.op === 'decline' && typeof m.request === 'string') settled.set(`${connection}\0${m.request}`, { at: Date.now() });
    for (const [k, r] of map) {
      if (r.connection !== connection) continue;
      if ((m.op === 'decline' && r.id === m.request) || (m.op === 'grant' && granted && r.subject && sameSubject(r.subject, granted))) {
        map.delete(k); settled.set(k, { at: Date.now(), created: r.created }); this.gone(user, r);
      }
    }
  }

  private async answer(user: string, c: RelayConnection, q: WireQuery, gen: number, token: number) {
    // When the answer must be on its way to the helper: its deadline, less the way there.
    const expires = typeof q.expires_in_ms === 'number' && Number.isFinite(q.expires_in_ms) && q.expires_in_ms >= 0 ? q.expires_in_ms : QUERY_MS;
    const deadline = this.now() + expires - SEND_MS;
    const guard = () => { if (!this.current(user, gen, token)) throw new Stopped(); };
    const query = { id: q.id, connection: c.id, tool: q.tool, subject: q.subject, args: (q.args && typeof q.args === 'object' && !Array.isArray(q.args) ? q.args : {}) } as RelayQuery;
    const refused = async () => { guard(); return refusal(await liveGrants(user), c.id, query.tool, query.subject); };
    let reply: Reply;
    try {
      const why = await refused();
      if (why) reply = { error: why };
      else {
        const { reply: r, port } = await this.handOff(user, query, gen, deadline);
        reply = r;
        if ('result' in reply) {
          const late = await refused();
          if (late) reply = { error: late };
          else if (port) this.remember(user, (reply.result as { next_cursor?: unknown } | null)?.next_cursor, port);
        }
      }
    } catch (e) {
      if (e instanceof Stopped) return;
      reply = { error: errorMessage(e) };
    }
    if (!this.current(user, gen, token)) return;
    await this.deps.request(user, c.peer, c.relay, { op: 'answer', route: c.route, id: q.id, ...bounded(c.route, q.id, reply) }, true).catch(() => {});
  }

  /** Remembers that `port` holds the snapshot a `next_cursor` continues. */
  private remember(user: string, cursor: unknown, port: Port) {
    const id = cursorSnapshot(cursor);
    if (!id) return;
    const k = `${user}\0${id}`;
    this.snapshots.delete(k); this.snapshots.set(k, port);
    while (this.snapshots.size > SNAPSHOTS) this.snapshots.delete(this.snapshots.keys().next().value!);
  }

  /**
   * The answer of one volunteering tab by `deadline`: the snapshot's tab, or
   * each in turn, passing over one that doesn't take the query in
   * RECEIPT_MS. A tab that took it is waited for until the deadline.
   */
  private async handOff(user: string, query: RelayQuery, gen: number, deadline: number): Promise<{ reply: Reply; port?: Port }> {
    const tried = new Set<Port>();
    const snapshot = (this.deps.affinity ?? snapshotOf)(query);
    const sticky = snapshot ? this.snapshots.get(`${user}\0${snapshot}`) : undefined;
    /** Waits for `p`, or `ms` at most. */
    const within = async <T>(p: Promise<T>, ms: number): Promise<T | 'timeout'> => {
      const timer = new AbortController();
      try { return await Promise.race([p, this.deps.delay(Math.max(0, ms), timer.signal).then(() => 'timeout' as const)]); }
      finally { timer.abort(); }
    };
    for (;;) {
      if (this.state(user).gen !== gen) throw new Stopped();
      const left = deadline - this.now();
      const serving = [...this.tabs].filter(([, t]) => t.user === user && t.serving).map(([p]) => p);
      const order = serving.map((_, i) => serving[(this.turn + i) % serving.length]).filter(p => !tried.has(p));
      let port = sticky && order.includes(sticky) ? sticky : undefined;
      port ??= order.find(p => !this.tabs.get(p)!.demoted) ?? order[0];
      if (!serving.length) return { reply: { error: NO_TAB } };
      // Too little left for a tab to answer back.
      if (left <= POST_MS) return { reply: { error: TOO_LATE } };
      // Every serving tab was asked, and none took it.
      if (!port) return { reply: { error: NO_TAB } };
      if (port !== sticky) this.turn = (serving.indexOf(port) + 1) % serving.length;
      tried.add(port);
      const qid = crypto.randomUUID();
      let received!: () => void;
      const took = new Promise<'received'>(r => { received = () => r('received'); });
      const answered = new Promise<Reply | null | 'stopped'>(resolve => this.waiting.set(qid, { port: port!, user, connection: query.connection, resolve, received }));
      port.postMessage({ event: 'query', user, qid, query, ms: left - POST_MS });
      try {
        let reply = await within(Promise.race([answered, took]), Math.min(RECEIPT_MS, left - POST_MS));
        if (reply === 'timeout') {
          const t = this.tabs.get(port);
          if (t) t.demoted = true;
          // Should it be reading after all, it stops: the next tab reads instead.
          port.postMessage({ event: 'cancel', user, qid });
          continue;
        }
        if (reply === 'received') {
          reply = await within(answered, deadline - this.now());
          if (reply === 'timeout') return { reply: { error: STILL_READING } };
        }
        if (reply === 'stopped') throw new Stopped();
        if (reply) return { reply, port };
      } finally { this.waiting.delete(qid); }
    }
  }
}

/** `reply` within what the helper takes: an answer past the wire limit becomes an error, and errors are cut short. */
function bounded(route: string, id: string, reply: Reply): { result: unknown } | { error: string } {
  if ('error' in reply) return { error: cut(String(reply.error) || 'Error', ERROR_LIMIT) };
  const result = reply.result ?? null;
  let size: number;
  try { size = bytes(JSON.stringify({ op: 'answer', route, id, result })); } catch { return { error: 'Answer could not be encoded' }; }
  return size > ANSWER_LIMIT ? { error: TOO_LARGE } : { result };
}
